import { Theme } from '@/utils/constants';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import clsx from 'clsx';
import React, { memo } from 'react';
import SwiperCore, { A11y, Autoplay, Navigation, Pagination, Scrollbar } from 'swiper';
import { Swiper } from 'swiper/react';
import Button from '../Button';
import { useShadowOffset } from './hooks/useShadowOffset';
import useStyles from './SwiperCarousel.styles';

SwiperCore.use([Navigation, Pagination, Scrollbar, A11y, Autoplay]);

const SliderNavigation = memo(({ classes, swiperClass, slider, sliderNavigationClass }: any) => {
  return (
    <div
      className={clsx(
        slider?.pagination?.bullets?.length <= 1 && 'disabled',
        classes.dotsArrowsContainer,
        swiperClass.navigation,
        'gtm-scroll',
        'swiper-dot-navigation',
      )}
    >
      <ChevronLeftIcon
        color="inherit"
        className={clsx(classes.dotsArrow, swiperClass.dotArrow, 'dot-arrow')}
        onClick={() => slider.slidePrev()}
      />
      <div
        className={clsx(swiperClass.dots, classes.sliderNavigation, sliderNavigationClass, 'dots')}
      />
      <ChevronRightIcon
        color="inherit"
        className={clsx(classes.dotsArrow, swiperClass.dotArrow, 'dot-arrow')}
        onClick={() => slider.slideNext()}
      />
    </div>
  );
});

const SwiperCarousel = React.forwardRef<unknown, any>(
  (
    {
      classes,
      children,
      navigation,
      slider,
      cta,
      theme = Theme.dark,
      mdTheme = Theme.dark,
      sliderNavigationClass,
      virtualTranslate,
      className,
      shadowOffset = false,
      shadowTheme = {
        mobile: Theme.dark,
        desktop: Theme.dark,
      },
      ...props
    },
    ref,
  ) => {
    const swiperClass = useStyles({});
    const rootClassName = clsx(
      swiperClass.root,
      `${theme}-theme`,
      `${mdTheme}-theme-md`,
      className,
    );
    const linkContainerClassName = clsx(
      swiperClass.linkContainer,
      classes.linkContainer,
      `${theme}-theme`,
      `${mdTheme}-theme-md`,
    );

    const { handleRealIndexChange, shadowStyleClasses } = useShadowOffset({
      shadowOffset,
      shadowTheme,
    });

    return (
      <div className={rootClassName}>
        <Swiper
          {...props}
          ref={ref}
          className={clsx(classes.swiper, shadowStyleClasses)}
          onSnapIndexChange={handleRealIndexChange}
        >
          {children}
          <div slot="container-end">
            <div className={clsx(swiperClass.footer, 'swiper-carousel-footer')}>
              {cta && cta?.displayText && (
                <Button theme={theme} className={linkContainerClassName} variant="link" link={cta}>
                  {cta.displayText}
                  <ChevronRightIcon color="inherit" fontSize="small" />
                </Button>
              )}
              {navigation && (
                <SliderNavigation
                  slider={slider}
                  classes={classes}
                  swiperClass={swiperClass}
                  sliderNavigationClass={sliderNavigationClass}
                />
              )}
            </div>
          </div>
        </Swiper>
      </div>
    );
  },
);

export default memo(SwiperCarousel);
